/**
 * useOnlineStatus - Hook to detect online/offline status
 * 
 * Features:
 * - Real-time online/offline detection
 * - Persists and serves cached data when offline
 * - Shows offline indicator
 */
import { useState, useEffect, useCallback } from 'react';

export function useOnlineStatus() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [wasOffline, setWasOffline] = useState(false);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      // Track that we came back online (for showing "back online" message)
      if (!isOnline) {
        setWasOffline(true);
        // Clear the "was offline" flag after 3 seconds
        setTimeout(() => setWasOffline(false), 3000);
      }
    };

    const handleOffline = () => {
      setIsOnline(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [isOnline]);

  return { isOnline, wasOffline };
}

/**
 * Cache manager for offline data storage
 */
const CACHE_KEYS = {
  POSTS: 'wlos_cached_posts',
  USER: 'wlos_cached_user',
  CONVERSATIONS: 'wlos_cached_conversations',
  LAST_SYNC: 'wlos_last_sync'
};

export function useCachedData() {
  // Save posts to localStorage
  const cachePosts = useCallback((posts) => {
    try {
      // Only cache the most recent 50 posts
      const postsToCache = posts.slice(0, 50);
      localStorage.setItem(CACHE_KEYS.POSTS, JSON.stringify(postsToCache));
      localStorage.setItem(CACHE_KEYS.LAST_SYNC, new Date().toISOString());
    } catch (error) {
      console.error('Error caching posts:', error);
    }
  }, []);

  // Get cached posts
  const getCachedPosts = useCallback(() => {
    try {
      const cached = localStorage.getItem(CACHE_KEYS.POSTS);
      return cached ? JSON.parse(cached) : [];
    } catch (error) {
      console.error('Error reading cached posts:', error);
      return [];
    }
  }, []);

  // Save user data to localStorage
  const cacheUser = useCallback((user) => {
    try {
      localStorage.setItem(CACHE_KEYS.USER, JSON.stringify(user));
    } catch (error) {
      console.error('Error caching user:', error);
    }
  }, []);

  // Get cached user
  const getCachedUser = useCallback(() => {
    try {
      const cached = localStorage.getItem(CACHE_KEYS.USER);
      return cached ? JSON.parse(cached) : null;
    } catch (error) {
      console.error('Error reading cached user:', error);
      return null;
    }
  }, []);

  // Get last sync time
  const getLastSyncTime = useCallback(() => {
    try {
      const lastSync = localStorage.getItem(CACHE_KEYS.LAST_SYNC);
      return lastSync ? new Date(lastSync) : null;
    } catch (error) {
      return null;
    }
  }, []);

  // Clear all cached data
  const clearCache = useCallback(() => {
    Object.values(CACHE_KEYS).forEach(key => {
      localStorage.removeItem(key);
    });
  }, []);

  return {
    cachePosts,
    getCachedPosts,
    cacheUser,
    getCachedUser,
    getLastSyncTime,
    clearCache
  };
}

export default useOnlineStatus;
